import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import LockedPage from '../components/LockedPage';
import PageHeader from '../components/PageHeader';
import NewsletterRendererV2 from '../components/NewsletterRendererV2';
import NewsletterEditorV2 from '../components/NewsletterEditorV2';

const TRIP_TYPES = ['Strand & Zon', 'Stad & Cultuur', 'Avontuur', 'Familie', 'Luxe', 'Wintersport', 'Rondreis'];
const TONES = ['Warm & Persoonlijk', 'Informatief', 'Enthousiast', 'Professioneel'];
const LANGUAGES = ['Dutch', 'English'];

const NL_TABS = [
  { id: 'generator', label: 'Nieuwsbrief' },
  { id: 'planning', label: 'Planning' },
  { id: 'marketing', label: 'Marketing' },
];

/* ── Light markdown renderer (shared with planning/marketing chat) ── */
function renderMarkdown(text) {
  const lines = text.split('\n');
  const elements = [];
  let i = 0;
  const inline = (str) => {
    let s = str.replace(/`([^`]+)`/g, '‹C›$1‹/C›').replace(/\*\*(.+?)\*\*/g, '‹B›$1‹/B›').replace(/\*(.+?)\*/g, '‹I›$1‹/I›');
    return s.split(/(‹C›.*?‹\/C›|‹B›.*?‹\/B›|‹I›.*?‹\/I›)/).map((tok, j) => {
      if (tok.startsWith('‹C›')) return <code key={j} className="bg-gray-100 px-1 rounded text-xs font-mono">{tok.slice(3, -4)}</code>;
      if (tok.startsWith('‹B›')) return <strong key={j}>{tok.slice(3, -4)}</strong>;
      if (tok.startsWith('‹I›')) return <em key={j}>{tok.slice(3, -3)}</em>;
      return tok;
    });
  };
  while (i < lines.length) {
    const line = lines[i];
    const hm = line.match(/^(#{1,3})\s+(.*)/);
    if (hm) {
      const lvl = hm[1].length;
      const cls = lvl === 1 ? 'text-lg font-bold mt-4 mb-2' : lvl === 2 ? 'text-base font-semibold mt-3 mb-1' : 'font-semibold mt-2';
      elements.push(<div key={i} className={cls}>{inline(hm[2])}</div>); i++; continue;
    }
    if (line.match(/^[-*•]\s+/)) {
      const items = [];
      while (i < lines.length && lines[i].match(/^[-*•]\s+/)) {
        items.push(<li key={i} className="ml-4 list-disc">{inline(lines[i].replace(/^[-*•]\s+/, ''))}</li>); i++;
      }
      elements.push(<ul key={`ul-${i}`} className="space-y-0.5 my-1">{items}</ul>); continue;
    }
    if (line.match(/^\d+\.\s+/)) {
      const items = [];
      while (i < lines.length && lines[i].match(/^\d+\.\s+/)) {
        items.push(<li key={i} className="ml-4 list-decimal">{inline(lines[i].replace(/^\d+\.\s+/, ''))}</li>); i++;
      }
      elements.push(<ol key={`ol-${i}`} className="space-y-0.5 my-1">{items}</ol>); continue;
    }
    if (line.match(/^---+$/)) { elements.push(<hr key={i} className="my-3 border-gray-200" />); i++; continue; }
    if (line.trim() === '') { elements.push(<div key={i} className="h-2" />); i++; continue; }
    elements.push(<div key={i}>{inline(line)}</div>); i++;
  }
  return elements;
}

/* ── Photo Library Modal ── */
function PhotoLibraryModal({ selectedUrls, onToggle, onClose }) {
  const { authFetch } = useAuth();
  const [hierarchy, setHierarchy] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    authFetch('/api/destination-photos').then(r => r.json()).then(d => {
      setHierarchy(d.hierarchy || {}); setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-sm w-full max-w-3xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div>
            <h3 className="font-bold text-gray-900">Foto bibliotheek — kies foto's voor de nieuwsbrief</h3>
            <p className="text-xs text-gray-400 mt-0.5">{selectedUrls.length} geselecteerd · klik om te selecteren/deselecteren</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="btn-primary text-sm">Bevestigen ({selectedUrls.length})</button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? (
            <div className="flex items-center justify-center h-32"><div className="spinner spinner-blue" /></div>
          ) : Object.keys(hierarchy).length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">Geen foto's in bibliotheek. Voeg foto's toe via Instellingen → Foto Bibliotheek.</div>
          ) : (
            Object.entries(hierarchy).map(([continent, countries]) => (
              <div key={continent}>
                <button onClick={() => setExpanded(e => ({ ...e, [continent]: !e[continent] }))}
                  className="w-full text-left px-3 py-2 rounded-lg font-semibold text-white text-sm mb-1"
                  style={{ backgroundColor: 'var(--brand)' }}>
                  {continent === 'Europa' ? '🌍' : '🌎'} {continent}
                </button>
                {expanded[continent] && Object.entries(countries).map(([country, areas]) => (
                  <div key={country} className="ml-3 mt-1 mb-2">
                    <p className="text-xs font-semibold text-gray-500 px-2 py-1 uppercase tracking-wide">{country}</p>
                    {Object.entries(areas).map(([area, photos]) => (
                      <div key={area} className="ml-2 mb-2">
                        <p className="text-xs text-gray-400 mb-1">📍 {area}</p>
                        <div className="flex gap-2 flex-wrap">
                          {photos.map(p => {
                            const selected = selectedUrls.includes(p.photo_url);
                            return (
                              <button key={p.id} onClick={() => onToggle(p.photo_url)}
                                className={`relative rounded-lg overflow-hidden border-2 transition-all ${selected ? 'border-blue-500 scale-105' : 'border-transparent hover:border-blue-300'}`}
                                style={{ width: 100, height: 70 }}>
                                <img src={p.photo_url} alt={p.title} className="w-full h-full object-cover"
                                  onError={e => { e.target.src = 'https://images.unsplash.com/photo-1488085061387-422e29b40080?w=200&q=60'; }} />
                                {selected && (
                                  <div className="absolute inset-0 bg-blue-500/30 flex items-center justify-center">
                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Chatbot Panel (shared for Planning + Marketing) ── */
function ChatbotPanel({ endpoint, planType, systemHint, placeholder, suggestions, onPlanSaved }) {
  const { authFetch } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveTitle, setSaveTitle] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  const lastAssistantContent = [...messages].reverse().find(m => m.role === 'assistant')?.content || '';

  const send = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    const newMsgs = [...messages, { role: 'user', content: msg }];
    setMessages(newMsgs);
    setLoading(true);
    try {
      const res = await authFetch(endpoint, { method: 'POST', body: JSON.stringify({ messages: newMsgs }) });
      let data;
      try { data = await res.json(); } catch { data = {}; }
      if (!res.ok) throw new Error(data.error || `Server fout (${res.status})`);
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply || 'Geen antwoord.' }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Er ging iets mis: ${err.message || 'Probeer opnieuw.'}` }]);
    }
    setLoading(false);
  }, [input, messages, loading, authFetch, endpoint]);

  const handleSave = async () => {
    if (!saveTitle.trim() || !lastAssistantContent) return;
    setSaving(true);
    try {
      const res = await authFetch('/api/newsletter/plans', {
        method: 'POST',
        body: JSON.stringify({ type: planType, title: saveTitle.trim(), content: lastAssistantContent }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSavedMsg('Plan opgeslagen!');
      setShowSaveDialog(false);
      setSaveTitle('');
      if (onPlanSaved) onPlanSaved();
      setTimeout(() => setSavedMsg(''), 3000);
    } catch (err) {
      setSavedMsg('Opslaan mislukt: ' + err.message);
    }
    setSaving(false);
  };

  return (
    <div className="flex flex-col h-full" style={{ minHeight: 500 }}>
      {/* messages */}
      <div className="flex-1 overflow-y-auto space-y-4 px-6 py-6 bg-gray-50 rounded-lg mb-4" style={{ minHeight: 380 }}>
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="text-4xl mb-3">💡</div>
            <p className="text-gray-500 text-sm font-medium">{systemHint}</p>
            <div className="mt-5 flex flex-wrap gap-2 justify-center">
              {suggestions.map((s, i) => (
                <button key={i} onClick={() => send(s)}
                  className="text-sm px-4 py-2 rounded-lg border border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm transition-all text-gray-600 hover:text-gray-900">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] px-4 py-3 rounded-lg text-sm leading-relaxed ${
              m.role === 'user' ? 'text-white rounded-br-sm' : 'bg-white text-gray-800 rounded-bl-sm border border-gray-100 shadow-sm'
            }`} style={m.role === 'user' ? { backgroundColor: 'var(--brand)' } : {}}>
              {m.role === 'assistant' ? renderMarkdown(m.content) : m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-white rounded-lg rounded-bl-sm px-4 py-3 border border-gray-100 shadow-sm">
              <div className="flex gap-1.5">
                {[0,1,2].map(d => <span key={d} className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: `${d*150}ms` }} />)}
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Save dialog */}
      {showSaveDialog && (
        <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex gap-2 items-center">
          <input
            className="input flex-1 text-sm py-2"
            placeholder="Naam voor dit plan..."
            value={saveTitle}
            onChange={e => setSaveTitle(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSave()}
            autoFocus
          />
          <button onClick={handleSave} disabled={!saveTitle.trim() || saving}
            className="btn-primary text-sm py-2 disabled:opacity-40">
            {saving ? <span className="spinner" /> : 'Opslaan'}
          </button>
          <button onClick={() => setShowSaveDialog(false)} className="text-gray-400 hover:text-gray-600 px-2">✕</button>
        </div>
      )}
      {savedMsg && <p className="text-sm text-green-600 font-medium mb-2 text-center">{savedMsg}</p>}

      {/* input row */}
      <div className="flex gap-2">
        <input
          className="input flex-1 text-sm"
          placeholder={placeholder}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), send())}
          disabled={loading}
        />
        <button onClick={() => send()} disabled={!input.trim() || loading}
          className="flex items-center justify-center w-10 h-10 rounded-lg text-white disabled:opacity-40 transition-all hover:opacity-90"
          style={{ backgroundColor: 'var(--brand)' }}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
        </button>
        {lastAssistantContent && !loading && (
          <button onClick={() => setShowSaveDialog(true)}
            className="px-3 py-2 text-xs font-medium text-blue-600 hover:text-blue-800 rounded-lg border border-blue-200 hover:bg-blue-50 transition-all whitespace-nowrap">
            Opslaan
          </button>
        )}
        {messages.length > 0 && (
          <button onClick={() => setMessages([])} className="px-3 py-2 text-xs text-gray-400 hover:text-gray-600 rounded-lg border border-gray-200 hover:bg-gray-50">
            Nieuw
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Plan Detail Modal ── */
function PlanDetailModal({ plan, onClose, onUse }) {
  const [period, setPeriod] = useState('');
  const [suggesting, setSuggesting] = useState(false);

  const handleUse = async () => {
    setSuggesting(true);
    await onUse(plan.id, period.trim() || null);
    setSuggesting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-sm w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs font-medium px-2 py-0.5 rounded ${plan.type === 'marketing' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                {plan.type === 'marketing' ? 'Marketing' : 'Planning'}
              </span>
              <span className="text-xs text-gray-400">{new Date(plan.created_at).toLocaleDateString('nl-NL')}</span>
            </div>
            <h3 className="font-bold text-gray-900">{plan.title}</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>

        {/* Plan content */}
        <div className="flex-1 overflow-y-auto p-5 text-sm text-gray-700 leading-relaxed">
          {renderMarkdown(plan.content)}
        </div>

        {/* Period selector + use button */}
        <div className="p-5 border-t border-gray-100 bg-gray-50 rounded-b-lg space-y-3">
          <div>
            <label className="label text-xs">Specifieke week of periode (optioneel)</label>
            <input
              className="input text-sm"
              placeholder="bijv. Week 3 – Mallorca, Week 1 vroegboek, Periode augustus..."
              value={period}
              onChange={e => setPeriod(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleUse()}
            />
            <p className="text-xs text-gray-400 mt-1">Laat leeg om de AI zelf de meest passende week te laten kiezen op basis van de datum.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={handleUse} disabled={suggesting}
              className="flex-1 btn-primary flex items-center justify-center gap-2">
              {suggesting ? <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> : null}
              {suggesting ? 'Formulier invullen...' : 'Genereer voor deze periode'}
            </button>
            <button onClick={onClose} className="btn-secondary">Sluiten</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Main Newsletter Component ── */
function NewsletterInner() {
  const { authFetch, user } = useAuth();
  const imageInputRef = useRef(null);
  const pdfInputRef = useRef(null);

  const [activeTab, setActiveTab] = useState('generator');
  const [showPhotoLibrary, setShowPhotoLibrary] = useState(false);
  const [libraryPhotos, setLibraryPhotos] = useState([]); // selected photo URLs from library

  const [form, setForm] = useState({
    subject: '', destination: '', trip_type: 'Strand & Zon',
    key_selling_point: '', tone: 'Warm & Persoonlijk', language: 'Dutch',
    urls: '', ai_instructions: '',
  });
  const [images, setImages] = useState([]);
  const [pdfTexts, setPdfTexts] = useState([]);
  const [pdfNames, setPdfNames] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [newsletterV2, setNewsletterV2] = useState(null);
  const [newsletterId, setNewsletterId] = useState(null);
  const [savedStatus, setSavedStatus] = useState(null); // null | 'saving' | 'saved' | 'unsaved'
  const [showEditor, setShowEditor] = useState(false);
  const [savedNewsletters, setSavedNewsletters] = useState([]);
  const [agencyName, setAgencyName] = useState('');
  const [error, setError] = useState('');
  const [sendModal, setSendModal] = useState(false);
  const [sendEmail, setSendEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState('');
  const [urlAsLink, setUrlAsLink] = useState(false);

  const [plans, setPlans] = useState([]);
  const [viewingPlan, setViewingPlan] = useState(null);
  const [planError, setPlanError] = useState('');

  const loadPlans = useCallback(async () => {
    try {
      const res = await authFetch('/api/newsletter/plans');
      const data = await res.json();
      setPlans(Array.isArray(data) ? data : []);
    } catch {}
  }, [authFetch]);

  const loadSavedNewsletters = useCallback(async () => {
    try {
      const res  = await authFetch('/api/newsletter');
      const data = await res.json();
      setSavedNewsletters(Array.isArray(data) ? data : []);
    } catch {}
  }, [authFetch]);

  useEffect(() => { loadPlans(); loadSavedNewsletters(); }, [loadPlans, loadSavedNewsletters]);

  const loadNewsletter = async (id) => {
    try {
      const res  = await authFetch(`/api/newsletter/by-id/${id}`);
      const data = await res.json();
      if (!res.ok) return;
      const nv2 = data.content_data?.newsletter_v2;
      if (!nv2) return; // v1 record — no editor support
      setNewsletterV2(nv2);
      setNewsletterId(id);
      setResult({ subject: nv2.header?.subject || data.subject });
      setSavedStatus('saved');
      setShowEditor(false);
    } catch {}
  };

  const handleNewsletterV2Change = (nv2) => {
    setNewsletterV2(nv2);
    if (savedStatus !== 'saving') setSavedStatus('unsaved');
  };

  const handleSave = async () => {
    if (!newsletterId || !newsletterV2 || savedStatus === 'saving') return;
    setSavedStatus('saving');
    try {
      const res = await authFetch(`/api/newsletter/${newsletterId}`, {
        method:  'PATCH',
        body:    JSON.stringify({ newsletter_v2: newsletterV2 }),
      });
      if (res.ok) {
        setSavedStatus('saved');
        loadSavedNewsletters();
      } else {
        setSavedStatus('unsaved');
      }
    } catch {
      setSavedStatus('unsaved');
    }
  };

  const handleUsePlan = async (planId, period) => {
    setPlanError('');
    try {
      const res = await authFetch('/api/newsletter/plans/suggest', {
        method: 'POST', body: JSON.stringify({ plan_id: planId, period: period || undefined }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Suggestie mislukt');
      const s = data.suggestion;
      setForm(prev => ({
        ...prev,
        destination: s.destination || prev.destination,
        trip_type: s.trip_type || prev.trip_type,
        key_selling_point: s.key_selling_point || prev.key_selling_point,
        tone: s.tone || prev.tone,
        subject: s.subject || prev.subject,
        ai_instructions: s.ai_instructions || prev.ai_instructions,
      }));
      setResult(null);
      setViewingPlan(null);
      setActiveTab('generator');
    } catch (err) {
      setPlanError(err.message);
    }
  };

  const handleDeletePlan = async (planId) => {
    await authFetch(`/api/newsletter/plans/${planId}`, { method: 'DELETE' });
    setPlans(prev => prev.filter(p => p.id !== planId));
    if (viewingPlan?.id === planId) setViewingPlan(null);
  };

  const handleDeleteNewsletter = async (id) => {
    await authFetch(`/api/newsletter/${id}`, { method: 'DELETE' });
    setSavedNewsletters(prev => prev.filter(n => n.id !== id));
  };

  const toggleLibraryPhoto = (url) => {
    setLibraryPhotos(prev => prev.includes(url) ? prev.filter(u => u !== url) : [...prev, url]);
  };

  const handleImageAdd = (e) => {
    const files = Array.from(e.target.files || []);
    setImages(prev => [...prev, ...files.map(f => ({ file: f, preview: URL.createObjectURL(f) }))]);
  };

  const handlePdfAdd = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      const fd = new FormData();
      fd.append('pdf', file);
      try {
        const res = await authFetch('/api/newsletter/extract-pdf', {
          method: 'POST', body: fd,
        });
        const data = await res.json();
        if (data.text) { setPdfTexts(prev => [...prev, data.text]); setPdfNames(prev => [...prev, file.name]); }
      } catch {}
    }
  };

  const removeImage = (idx) => setImages(prev => prev.filter((_, i) => i !== idx));
  const removePdf = (idx) => {
    setPdfTexts(prev => prev.filter((_, i) => i !== idx));
    setPdfNames(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSendNewsletter = async () => {
    if (!sendEmail.trim() || !newsletterV2) return;
    setSending(true); setSendMsg('');
    try {
      const nv2     = newsletterV2;
      const h       = nv2.header        || {};
      const it      = nv2.intro         || {};
      const sg      = nv2.signoff       || {};
      const cta     = nv2.cta           || {};
      const bs      = nv2.branding_snapshot || {};
      const brandColor = '#1e3a5f';

      const sectionsHtml = (nv2.sections || []).map(s => `
        <tr><td style="padding:28px 32px;border-bottom:1px solid #e5e7eb;">
          ${s.photo_url ? `<img src="${s.photo_url}" alt="" width="536" style="width:100%;max-width:536px;display:block;border:0;margin-bottom:16px;" />` : ''}
          <h2 style="margin:0 0 12px;font-size:15px;font-weight:600;color:#111827;font-family:Arial,sans-serif;">${s.title || ''}</h2>
          ${s.highlight ? `<p style="border-left:3px solid ${brandColor};padding:6px 14px;margin:0 0 12px;font-style:italic;color:#6b7280;font-size:13px;font-family:Arial,sans-serif;">${s.highlight}</p>` : ''}
          <p style="margin:0;color:#374151;font-size:14px;line-height:1.6;font-family:Arial,sans-serif;">${s.body || ''}</p>
        </td></tr>`).join('');

      const dealsHtml = (nv2.deals || []).length > 0 ? `
        <tr><td style="padding:28px 32px;background:#f9fafb;border-bottom:1px solid #e5e7eb;">
          <h2 style="margin:0 0 16px;font-size:15px;font-weight:600;color:#111827;font-family:Arial,sans-serif;">Uitgelichte aanbiedingen</h2>
          ${(nv2.deals || []).map(d => `
            <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e5e7eb;margin-bottom:10px;">
              <tr><td style="padding:16px;">
                <strong style="font-size:14px;color:#111827;font-family:Arial,sans-serif;">${d.destination || ''}</strong>
                ${d.tagline ? `<p style="margin:4px 0 0;font-size:13px;color:#6b7280;font-family:Arial,sans-serif;">${d.tagline}</p>` : ''}
                ${d.price_from ? `<p style="margin:8px 0 0;font-size:18px;font-weight:700;color:${brandColor};font-family:Arial,sans-serif;">Vanaf &#8364;${d.price_from}</p>` : ''}
              </td></tr>
            </table>`).join('')}
        </td></tr>` : '';

      const ctaHtml = (cta.visible && cta.text) ? `
        <tr><td style="padding:28px 32px;text-align:center;border-bottom:1px solid #e5e7eb;">
          ${cta.note ? `<p style="color:#6b7280;font-size:14px;margin:0 0 16px;font-family:Arial,sans-serif;">${cta.note}</p>` : ''}
          <a href="${cta.url || '#'}" style="background:${brandColor};color:#ffffff;padding:12px 28px;font-weight:600;font-size:14px;text-decoration:none;display:inline-block;font-family:Arial,sans-serif;">${cta.text}</a>
        </td></tr>` : '';

      const html = `<!DOCTYPE html><html lang="nl"><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/></head><body style="margin:0;padding:0;background:#f3f4f6;">
<table width="100%" cellpadding="0" cellspacing="0" bgcolor="#f3f4f6"><tr><td align="center" style="padding:32px 16px;">
<table width="600" cellpadding="0" cellspacing="0" bgcolor="#ffffff" style="max-width:600px;width:100%;border:1px solid #e5e7eb;">
  <tr><td bgcolor="${brandColor}" style="padding:28px 32px;">
    <h1 style="margin:0;color:#ffffff;font-size:22px;font-weight:600;line-height:1.3;font-family:Arial,sans-serif;">${h.headline || ''}</h1>
    ${h.preheader ? `<p style="margin:8px 0 0;color:rgba(255,255,255,0.6);font-style:italic;font-size:14px;font-family:Arial,sans-serif;">${h.preheader}</p>` : ''}
    ${bs.agency_name ? `<p style="margin:16px 0 0;color:rgba(255,255,255,0.35);font-size:12px;font-family:Arial,sans-serif;">${bs.agency_name}</p>` : ''}
  </td></tr>
  ${it.body ? `<tr><td style="padding:28px 32px;border-bottom:1px solid #e5e7eb;"><p style="margin:0;color:#374151;font-size:14px;line-height:1.6;font-family:Arial,sans-serif;">${it.body}</p></td></tr>` : ''}
  ${sectionsHtml}
  ${dealsHtml}
  ${ctaHtml}
  ${sg.body ? `<tr><td style="padding:28px 32px;border-bottom:1px solid #e5e7eb;"><p style="margin:0;color:#6b7280;font-size:14px;line-height:1.6;font-family:Arial,sans-serif;">${sg.body}</p></td></tr>` : ''}
  <tr><td style="padding:20px 32px;border-top:1px solid #e5e7eb;">
    <table width="100%" cellpadding="0" cellspacing="0"><tr>
      <td style="font-size:12px;color:#9ca3af;font-family:Arial,sans-serif;">${[bs.agency_name, bs.agent_name].filter(Boolean).join(' &middot; ')}</td>
      <td align="right" style="font-size:12px;color:#9ca3af;font-family:Arial,sans-serif;">${[bs.phone || '', bs.website_url || ''].filter(Boolean).join('  ')}</td>
    </tr></table>
  </td></tr>
</table>
</td></tr></table>
</body></html>`;
      const res = await authFetch('/api/newsletter/send', {
        method: 'POST',
        body: JSON.stringify({ recipient: sendEmail.trim(), subject: newsletterV2?.header?.subject || 'Nieuwsbrief', html }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error(`Server fout (${res.status})`); }
      if (!res.ok) throw new Error(data?.error || `Server fout (${res.status})`);
      setSendMsg('Verstuurd!');
      setTimeout(() => { setSendModal(false); setSendMsg(''); setSendEmail(''); }, 2000);
    } catch (err) {
      setSendMsg('Fout: ' + err.message);
    }
    setSending(false);
  };

const handleGenerate = async () => {
  setError('');
  setGenerating(true);
  setResult(null);
  setNewsletterV2(null);
  setNewsletterId(null);
  setSavedStatus(null);
  setShowEditor(false);

  const fd = new FormData();
  Object.entries(form).forEach(([k, v]) => fd.append(k, v));
  images.forEach((img) => fd.append('images', img.file));
  pdfTexts.forEach((t) => fd.append('pdf_texts', t));
  libraryPhotos.forEach((url) => fd.append('photo_urls', url));

  try {
    const res = await authFetch('/api/newsletter/generate', {
      method: 'POST',
      body: fd,
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Generatie mislukt');

    setResult(data.content);
    setNewsletterV2(data.newsletter_v2 || null);
    setNewsletterId(data.newsletterId || null);
    setSavedStatus(data.newsletterId ? 'saved' : null);
    setAgencyName(data.agencyName || user?.agency_name || '');
    loadSavedNewsletters();
  } catch (err) {
    setError(err.message || 'Generatie mislukt');
  } finally {
    setGenerating(false);
  }
};

  const copyNewsletter = () => {
    if (!result) return;
    const text = [
      `ONDERWERP: ${result.subject}`, `PREVIEW: ${result.preheader}`, '', result.headline, '',
      result.intro, '',
      ...(result.sections || []).flatMap(s => [s.title, s.body, '']),
      result.cta_note, `[${result.cta_text}]`, '', result.signoff,
    ].join('\n');
    navigator.clipboard.writeText(text);
  };

  const serverImages = result?.images || [];
  const allPhotos = [...libraryPhotos];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <PageHeader title="Nieuwsbrief" subtitle="Genereer nieuwsbrieven, maak een redactiekalender of schrijf een marketingplan" />
      <div className="p-8 page-enter" style={{ flex: 1, overflowY: 'auto' }}>

      {/* Tab bar */}
      <div className="flex border-b border-gray-200 mb-6">
        {NL_TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={activeTab === tab.id
              ? 'px-4 py-2.5 text-sm font-medium border-b-2 text-gray-900 -mb-px'
              : 'px-4 py-2.5 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-gray-700'}
            style={activeTab === tab.id ? { borderColor: 'var(--brand)' } : {}}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── GENERATOR TAB ── */}
      {activeTab === 'generator' && (
        <>
          {/* Saved Plans */}
          {plans.length > 0 && !result && (
            <div className="pro-card p-5 mb-6">
              <h2 className="font-semibold text-gray-900 mb-3">Opgeslagen plannen</h2>
              {planError && <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{planError}</div>}
              <div className="space-y-2">
                {plans.map(plan => (
                  <div key={plan.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded shrink-0 ${plan.type === 'marketing' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                      {plan.type === 'marketing' ? 'Marketing' : 'Planning'}
                    </span>
                    <button onClick={() => setViewingPlan(plan)} className="flex-1 text-sm font-medium text-gray-800 truncate text-left hover:text-blue-600 transition-colors">
                      {plan.title}
                    </button>
                    <span className="text-xs text-gray-400 shrink-0">{new Date(plan.created_at).toLocaleDateString('nl-NL')}</span>
                    <button
                      onClick={() => setViewingPlan(plan)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-lg transition-all hover:opacity-90 shrink-0"
                      style={{ backgroundColor: 'var(--brand)' }}>
                      Gebruik plan
                    </button>
                    <button onClick={() => handleDeletePlan(plan.id)} className="text-gray-300 hover:text-red-400 transition-colors text-sm shrink-0">✕</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* History panel — shown when no newsletter is active */}
          {!result && savedNewsletters.length > 0 && (
            <div className="pro-card p-5 mb-6">
              <h2 className="font-semibold text-gray-900 mb-3">Eerdere nieuwsbrieven</h2>
              <div className="space-y-2">
                {savedNewsletters.slice(0, 8).map(nl => (
                  <div key={nl.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors">
                    <span className="text-xs text-gray-400 shrink-0">
                      {new Date(nl.created_at).toLocaleDateString('nl-NL')}
                    </span>
                    <span className="flex-1 text-sm text-gray-700 truncate">{nl.subject}</span>
                    <button
                      onClick={() => loadNewsletter(nl.id)}
                      className="text-xs px-3 py-1.5 rounded-lg text-white transition-all hover:opacity-90 shrink-0"
                      style={{ backgroundColor: 'var(--brand)' }}>
                      Openen
                    </button>
                    <button onClick={() => handleDeleteNewsletter(nl.id)} className="text-gray-300 hover:text-red-400 transition-colors text-sm shrink-0">✕</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!result ? (
            <div className="pro-card p-5 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="md:col-span-2">
                  <label className="label">Onderwerpregel nieuwsbrief</label>
                  <input className="input" placeholder="bijv. Zomerse aanbiedingen — pak nu uw droomvakantie!" value={form.subject}
                    onChange={e => setForm({ ...form, subject: e.target.value })} />
                </div>
                <div>
                  <label className="label">Bestemming / Thema</label>
                  <input className="input" placeholder="bijv. Mallorca, Zomeraanbiedingen, Caraïben..." value={form.destination}
                    onChange={e => setForm({ ...form, destination: e.target.value })} />
                </div>
                <div>
                  <label className="label">Type reis</label>
                  <select className="input" value={form.trip_type} onChange={e => setForm({ ...form, trip_type: e.target.value })}>
                    {TRIP_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="label">Belangrijkste verkoopargument</label>
                  <input className="input" placeholder="bijv. Exclusieve early bird-kortingen tot 30% — alleen deze week" value={form.key_selling_point}
                    onChange={e => setForm({ ...form, key_selling_point: e.target.value })} />
                </div>
                <div>
                  <label className="label">Toon</label>
                  <select className="input" value={form.tone} onChange={e => setForm({ ...form, tone: e.target.value })}>
                    {TONES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Taal</label>
                  <select className="input" value={form.language} onChange={e => setForm({ ...form, language: e.target.value })}>
                    {LANGUAGES.map(l => <option key={l}>{l}</option>)}
                  </select>
                </div>
              </div>

              {/* Assets */}
              <div className="border-t border-gray-100 pt-4 mt-2">
                <p className="text-sm font-semibold text-gray-700 mb-3">Bronmateriaal &amp; Foto's</p>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  {/* Photo library */}
                  <button type="button" onClick={() => setShowPhotoLibrary(true)}
                    className="border-2 border-dashed border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors flex flex-col items-center gap-2 text-center">
                    <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
                      <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-700">Foto bibliotheek</span>
                    <span className="text-xs text-gray-400">{libraryPhotos.length > 0 ? `${libraryPhotos.length} gekozen` : 'Kies bestemmingsfoto\'s'}</span>
                  </button>

                  {/* Upload images */}
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                    <button type="button" onClick={() => imageInputRef.current?.click()}
                      className="w-full flex flex-col items-center gap-2 text-center">
                      <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                        <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <span className="text-sm font-medium text-gray-700">Afbeeldingen uploaden</span>
                      <span className="text-xs text-gray-400">JPG, PNG, WebP</span>
                    </button>
                    <input ref={imageInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleImageAdd} />
                  </div>

                  {/* PDF upload */}
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                    <button type="button" onClick={() => pdfInputRef.current?.click()}
                      className="w-full flex flex-col items-center gap-2 text-center">
                      <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                        <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <span className="text-sm font-medium text-gray-700">PDF-documenten</span>
                      <span className="text-xs text-gray-400">Tekst wordt geëxtraheerd</span>
                    </button>
                    <input ref={pdfInputRef} type="file" accept=".pdf" multiple className="hidden" onChange={handlePdfAdd} />
                  </div>

                  {/* URL input */}
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-4">
                    <div className="flex flex-col items-center gap-2 text-center mb-2">
                      <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
                        <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                      </div>
                      <span className="text-sm font-medium text-gray-700">URL's</span>
                    </div>
                    <textarea className="input text-xs min-h-16" placeholder={"https://example.com/aanbieding"}
                      value={form.urls} onChange={e => setForm({ ...form, urls: e.target.value })} />
                    {form.urls.trim() && (
                      <label className="flex items-center gap-2 mt-2 text-xs text-gray-600 cursor-pointer">
                        <input type="checkbox" checked={urlAsLink} onChange={e => setUrlAsLink(e.target.checked)} className="rounded" />
                        Voeg klikbare link toe in nieuwsbrief
                      </label>
                    )}
                  </div>
                </div>

                {/* Selected library photos */}
                {libraryPhotos.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-2">Bibliotheek foto's geselecteerd ({libraryPhotos.length})</p>
                    <div className="flex gap-2 flex-wrap">
                      {libraryPhotos.map((url, i) => (
                        <div key={i} className="relative group">
                          <img src={url} alt="" className="w-20 h-14 object-cover rounded-lg border border-gray-200"
                            onError={e => { e.target.style.display = 'none'; }} />
                          <button onClick={() => setLibraryPhotos(prev => prev.filter((_, j) => j !== i))}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Uploaded images */}
                {images.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-2">Geüploade afbeeldingen ({images.length})</p>
                    <div className="flex gap-2 flex-wrap">
                      {images.map((img, i) => (
                        <div key={i} className="relative group">
                          <img src={img.preview} alt="" className="w-20 h-14 object-cover rounded-lg border border-gray-200" />
                          <button onClick={() => removeImage(i)}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {pdfNames.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-2">PDFs verwerkt ({pdfNames.length})</p>
                    <div className="flex gap-2 flex-wrap">
                      {pdfNames.map((name, i) => (
                        <div key={i} className="flex items-center gap-1 px-3 py-1 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                          {name}
                          <button onClick={() => removePdf(i)} className="ml-1 text-red-400 hover:text-red-600">×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* AI Instructions */}
              <div className="border-t border-gray-100 pt-4 mt-4">
                <label className="label flex items-center gap-1.5">
                  Instructies voor de AI
                  <span className="text-xs font-normal text-gray-400 ml-1">— optioneel</span>
                </label>
                <textarea className="input text-sm min-h-20 resize-none"
                  placeholder={`Bijv.:\n• "Vermeld altijd onze vroegboekkorting van 15%"\n• "Sluit af met verwijzing naar onze website"`}
                  value={form.ai_instructions} onChange={e => setForm({ ...form, ai_instructions: e.target.value })} />
              </div>

              {error && <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
              <div className="mt-5">
                <button onClick={handleGenerate} disabled={generating} className="btn-primary">
                  {generating ? <span className="spinner" /> : null}
                  {generating ? 'Nieuwsbrief genereren...' : 'Nieuwsbrief genereren'}
                </button>
              </div>
            </div>
          ) : (
            <div>
              {/* Toolbar */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <h2 className="text-base font-semibold text-gray-900">Nieuwsbrief</h2>
                  {savedStatus === 'unsaved' && (
                    <span className="text-xs text-amber-600 font-medium">Niet opgeslagen</span>
                  )}
                  {savedStatus === 'saved' && (
                    <span className="text-xs text-green-600 font-medium">✓ Opgeslagen</span>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap justify-end">
                  <button onClick={() => setShowEditor(e => !e)} className="btn-secondary text-sm">
                    {showEditor ? 'Verberg editor' : 'Bewerken'}
                  </button>
                  {savedStatus === 'unsaved' && newsletterId && (
                    <button onClick={handleSave}
                      className="btn-secondary text-sm border-amber-300 text-amber-700 hover:bg-amber-50">
                      {savedStatus === 'saving' ? <span className="spinner" style={{ width: 12, height: 12, borderWidth: 1.5 }} /> : 'Wijzigingen opslaan'}
                    </button>
                  )}
                  <button onClick={() => { setSendModal(true); setSendMsg(''); }} className="btn-secondary text-sm">
                    Verstuur per e-mail
                  </button>
                  <button onClick={() => {
                    setResult(null); setNewsletterV2(null); setNewsletterId(null);
                    setSavedStatus(null); setShowEditor(false);
                    setImages([]); setPdfTexts([]); setPdfNames([]); setLibraryPhotos([]);
                  }} className="btn-secondary text-sm">
                    Nieuwe nieuwsbrief
                  </button>
                </div>
              </div>

              {/* Editor + Preview split */}
              <div className={showEditor ? 'grid grid-cols-1 lg:grid-cols-2 gap-6 items-start' : ''}>
                {showEditor && newsletterV2 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Bewerken</p>
                    <NewsletterEditorV2
                      newsletter_v2={newsletterV2}
                      onChange={handleNewsletterV2Change}
                    />
                  </div>
                )}
                <div>
                  {showEditor && <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Preview</p>}
                  <NewsletterRendererV2 newsletter_v2={newsletterV2} />
                </div>
              </div>
            </div>
          )}

          {generating && !result && (
            <div className="pro-card p-5 text-center py-16">
              <div className="spinner spinner-blue mx-auto mb-4" style={{ width: 36, height: 36, borderWidth: 3 }} />
              <p className="text-gray-600 font-medium">Uw nieuwsbrief wordt geschreven...</p>
              <p className="text-gray-400 text-sm mt-1">Dit kan 15-30 seconden duren</p>
            </div>
          )}
        </>
      )}

      {/* ── PLANNING TAB ── */}
      {activeTab === 'planning' && (
        <div className="pro-card p-5">
          <div className="mb-5">
            <h2 className="font-semibold text-gray-900">Nieuwsbrief Planning</h2>
            <p className="text-gray-400 text-xs mt-1">Beschrijf een thema en het aantal weken — de AI maakt een gedetailleerde redactiekalender</p>
          </div>
          <ChatbotPanel
            endpoint="/api/newsletter/planning"
            planType="planning"
            onPlanSaved={loadPlans}
            systemHint="Vertel me over het thema en de periode waarvoor u een nieuwsbrief-planning wilt maken"
            placeholder="bijv. 'Maak een planning voor de zomercampagne, 8 weken, rondom Middellandse Zee bestemmingen'"
            suggestions={[
              'Plan 6 weken nieuwsbrieven rondom zomervakantie bestemmingen',
              'Maak een planning voor het winterseizoen, 8 weken',
              'Schrijf een 4-weken planning voor familievakantie aanbiedingen',
              'Plan nieuwsbrieven voor het nieuwe jaar, januari–maart',
            ]}
          />
        </div>
      )}

      {/* ── MARKETING TAB ── */}
      {activeTab === 'marketing' && (
        <div className="pro-card p-5">
          <div className="mb-5">
            <h2 className="font-semibold text-gray-900">Marketing &amp; Klantbereik</h2>
            <p className="text-gray-400 text-xs mt-1">Laat de AI een marketingplan schrijven op basis van een thema of campagne-idee</p>
          </div>
          <ChatbotPanel
            endpoint="/api/newsletter/marketing"
            planType="marketing"
            onPlanSaved={loadPlans}
            systemHint="Beschrijf uw campagne-idee of doelgroep en ik schrijf een marketingplan"
            placeholder="bijv. 'Schrijf een marketingplan voor luxe reizen naar de Caraïben, gericht op 40+ koppels'"
            suggestions={[
              'Marketingplan voor gezinsvakanties naar Spanje dit jaar',
              'Klantbereik-strategie voor luxe reizen, doelgroep 50+',
              'Plan voor het heractiveren van slapende klanten',
              'Campagne-ideeën voor vroegboekseizoen',
            ]}
          />
        </div>
      )}

      {/* Photo Library Modal */}
      {showPhotoLibrary && (
        <PhotoLibraryModal
          selectedUrls={libraryPhotos}
          onToggle={toggleLibraryPhoto}
          onClose={() => setShowPhotoLibrary(false)}
        />
      )}

      {/* Send Modal */}
      {sendModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-sm w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Verstuur nieuwsbrief</h3>
              <button onClick={() => setSendModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <label className="label">Ontvanger e-mailadres</label>
            <input className="input mb-4" type="email" placeholder="klant@example.com" value={sendEmail}
              onChange={e => setSendEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSendNewsletter()} autoFocus />
            {sendMsg && <p className={`text-sm mb-3 ${sendMsg.startsWith('Fout') ? 'text-red-600' : 'text-green-600'}`}>{sendMsg}</p>}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setSendModal(false)} className="btn-secondary">Annuleren</button>
              <button onClick={handleSendNewsletter} disabled={!sendEmail.trim() || sending} className="btn-primary disabled:opacity-40">
                {sending ? <span className="spinner" /> : 'Versturen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plan Detail Modal */}
      {viewingPlan && (
        <PlanDetailModal
          plan={viewingPlan}
          onClose={() => setViewingPlan(null)}
          onUse={handleUsePlan}
        />
      )}
      </div>
    </div>
  );
}

export default function Newsletter() {
  const { hasAccess } = useSubscription();
  if (!hasAccess('bureau')) return <LockedPage featureName="Nieuwsbrief"><NewsletterInner /></LockedPage>;
  return <NewsletterInner />;
}
